import { useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useMatch } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { MDSIcon, MDSPlainButton, MDSTooltip } from '@marqvision/mds-v2';
import { useLocalStorage } from '@marqvision/shared';
import { useUserInfoQuery } from '~/apis/auth';
import { useLogger } from '~/hooks';
import { Type } from './@types';
import { LOG_EVENT_NAME } from './@constants';
import { Wrapper } from './Wrapper';

const useStyles = makeStyles(({ color, _raw_color }) => ({
  wrapper: {
    position: 'relative',
    display: 'grid',
  },
  new: {
    position: 'absolute',
    top: '-0.5px',
    left: '-2.5px',
    width: '9px',
    height: '9px',
    borderRadius: '50%',
    backgroundClip: 'content-box',
    backgroundColor: _raw_color.red700,
    border: `1.5px solid ${color.bg.surface.neutral.default.normal}`,
  },
}));

export const ReleaseNote = () => {
  const classes = useStyles();
  const matches = useMatch('/home');
  const { amplitudeLogging } = useLogger();
  const [lastReleaseNoteDate, setLastReleaseNoteDate] = useLocalStorage<string>('last-release-note-date', '');

  const { data: userInfo } = useUserInfoQuery();

  const anchorRef = useRef<HTMLDivElement>(null);
  const [type, setType] = useState<Type>();

  const isNew = useMemo(() => {
    return (
      !!userInfo?.last_release_note_date &&
      (!lastReleaseNoteDate || dayjs(userInfo.last_release_note_date).diff(lastReleaseNoteDate, 'd') > 0)
      // 저장된 마지막 릴리즈 노트 정보가 없거나, 저장된 정보보다 서버에서 전달받은 정보가 최신일 때 new 딱지 출력
    );
  }, [lastReleaseNoteDate, userInfo]);

  const updateLastReleaseNoteDate = () => {
    if (!userInfo?.last_release_note_date) return;
    setLastReleaseNoteDate(userInfo.last_release_note_date);
  };

  const handleOpen = (location: 'header' | 'popup') => () => {
    amplitudeLogging(LOG_EVENT_NAME.viewedReleaseNoteList, { 'Button location': location });
    setType('release-note');
    updateLastReleaseNoteDate();
  };

  const handleClose = () => {
    setType(undefined);
  };

  useEffect(() => {
    if (!userInfo) return;
    setType((prev) => (prev === 'release-note' ? prev : matches && isNew ? 'popup' : undefined));
  }, [userInfo, matches]); // intentional missing deps

  return (
    <>
      <MDSTooltip title="Release note" size="small" position="bottom-center" style={{ marginTop: '4px' }}>
        <div ref={anchorRef} className={classes.wrapper}>
          <MDSPlainButton onClick={handleOpen('header')}>
            <MDSIcon.Rocket size={20} />
          </MDSPlainButton>
          {isNew && <i className={classes.new} />}
        </div>
      </MDSTooltip>
      {anchorRef.current && type && (
        <Wrapper anchor={anchorRef.current} type={type} onExplore={handleOpen('popup')} onClose={handleClose} />
      )}
    </>
  );
};
